import React from "react";
import { Box, Typography } from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import { cardInitial, cardInView, cardTransition } from "./HeroAnimations";

type CardConfig = {
  title: string;
  titleColor: string;
  items: string[];
  markerColor: string;
  marker: "check" | "dot";
  headerDots?: string[];
  markerSize?: number;
  markerGap?: number;
  itemSpacing?: number;
  itemLineHeight?: number;
};

const VALUE_CARDS: CardConfig[] = [
  {
    title: "METERED BILLING",
    titleColor: "#64748b",
    markerColor: "#22c55e",
    marker: "check",
    markerSize: 6,
    markerGap: 1.375,
    itemSpacing: 1,
    itemLineHeight: 1,
    items: [
      "Pay only for what you use",
      "No fixed plans, no commitments",
      "Zero cloud markup",
    ],
  },
  {
    title: "SHIP FASTER",
    titleColor: "#64748b",
    markerColor: "#00d4ff",
    marker: "dot",
    markerSize: 8,
    markerGap: 1,
    itemSpacing: 1,
    itemLineHeight: 1.3,
    items: [
      "Build to prod in one workflow",
      "Automated GitOps delivery",
      "From commit to production",
    ],
  },
  {
    title: "BRING YOUR CLOUD",
    titleColor: "#f59e0b",
    markerColor: "#f59e0b",
    marker: "dot",
    markerSize: 8,
    markerGap: 1,
    itemSpacing: 1,
    itemLineHeight: 1.3,
    headerDots: ["#fbbf24", "#60a5fa", "#f97316"],
    items: [
      "AWS, Azure, GCP — your cloud",
      "FinOps cost visibility",
      "Your cloud, your rules",
    ],
  },
  {
    title: "SELF-HEALING",
    titleColor: "#6366f1",
    markerColor: "#6366f1",
    marker: "dot",
    markerSize: 8,
    markerGap: 1,
    itemSpacing: 1,
    itemLineHeight: 1.3,
    items: [
      "Auto-remediation on failure",
      "Zero-downtime recovery",
      "Health-aware orchestration",
    ],
  },
];

const HeroValueCards: React.FC = () => {
  return (
    <Box
      sx={{
        width: { xs: "100%", md: 215 },
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      {VALUE_CARDS.map((card, index) => (
        <MotionBox
          key={card.title}
          component="article"
          initial={cardInitial}
          whileInView={cardInView}
          viewport={{ once: true }}
          transition={cardTransition(index)}
          sx={{
            minHeight: 96,
            px: 2.25,
            py: 1.5,
            borderRadius: "10px",
            border: "1px solid #1e293b",
            background:
              "linear-gradient(135deg, rgba(15,29,53,0.97) 0%, rgba(10,20,40,0.99) 100%)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              component="h3"
              sx={{
                m: 0,
                fontSize: "9.5px",
                fontWeight: 600,
                letterSpacing: "0.08em",
                color: card.titleColor,
              }}
            >
              {card.title}
            </Typography>
            {card.headerDots ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                {card.headerDots.map((dot) => (
                  <Box
                    key={dot}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: dot,
                      opacity: 0.6,
                    }}
                  />
                ))}
              </Box>
            ) : null}
          </Box>

          <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
            {card.items.map((item) => (
              <Box
                component="li"
                key={item}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: card.markerGap ?? 1,
                  mb: card.itemSpacing ?? 1,
                  "&:last-of-type": { mb: 0 },
                }}
              >
                {card.marker === "check" ? (
                  <Box
                    component="svg"
                    viewBox="0 0 6 6"
                    width={card.markerSize ?? 6}
                    height={card.markerSize ?? 6}
                    sx={{ overflow: "visible", flexShrink: 0 }}
                  >
                    <circle
                      cx="3"
                      cy="3"
                      r="3"
                      fill={card.markerColor}
                      fillOpacity="0.2"
                    />
                    <path
                      d="M1 3 L3 5 L5 1"
                      fill="none"
                      stroke={card.markerColor}
                      strokeWidth="0.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      position: "relative",
                      width: card.markerSize ?? 8,
                      height: card.markerSize ?? 8,
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        bgcolor: card.markerColor,
                        opacity: 0.2,
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: 2.5,
                        left: 2.5,
                        width: 3,
                        height: 3,
                        borderRadius: "50%",
                        bgcolor: card.markerColor,
                      }}
                    />
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: "10px",
                    lineHeight: card.itemLineHeight ?? 1.3,
                    color: "#e2e8f0",
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </MotionBox>
      ))}
    </Box>
  );
};

export default HeroValueCards;
